/* eslint-disable no-console */
/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useState } from 'react';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import styled from 'styled-components';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@panel/commons/hooks';
import { useSnackbar, SNACKBAR_TYPE } from '@panel/commons/lib/snackbar';
import { site } from '@panel/commons/helpers/siteUtils';
import { useLoader } from '@panel/commons/lib/Loader/useLoader';
import { Loader } from '@panel/commons/lib/Loader/Loader';
import { useSelector } from 'react-redux';
import { Step1Module4Selectors } from '../../../../../../ducks/step1Module4';
import { sendPaymentNotification } from '../../../../../../utils';

const Container = styled.div`
  width: 100%;
  margin: 0px auto;
`;

const orderDefaults = {
  brand_name: 'Navent LTD.',
  shipping_preference: 'NO_SHIPPING'
};

const formatPrice = stringPrice => {
  return stringPrice ? stringPrice.replaceAll(',', '.') : '0.00';
};

const paypalDefaults = {
  'client-id': process.env.REACT_APP_PAYPAL_CLIENT_TOKEN,
  locale: 'es_PA',
  // 'merchant-id': '',
  currency: 'USD',
  intent: 'capture' // capture (DEFAULT), authorize, subscription, tokenize
};

function PaypalCheckout({ order, product, paymentInfo, enabled = false }) {
  const [handshakeId, setHandshakeId] = useState('');
  const { formValues } = useSelector(Step1Module4Selectors.getStep1Module4State);
  const [postingId] = useQuery(useLocation(), ['postingId']);
  const { setSnackBar, setToggleSnack } = useSnackbar();
  const { toggleLoader, setToggleLoader } = useLoader();
  const history = useHistory();
  const { t } = useTranslation();
  const currency = 'USD'; // tomarlo de currenciesBySite??

  useEffect(() => {
    const isCreatingOrder = !order || !order.orderId;
    setToggleLoader(isCreatingOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  const sendMail = async data => {
    const mailData = {
      orderId: data.idOrder,
      postingTitle: formValues.title,
      postingId
    };
    await sendPaymentNotification('PAYPAL', mailData);
  };

  const activateOrder = async (paymentData, paymentDetails) => {
    if (paymentData && paymentDetails && paymentDetails.status === 'COMPLETED') {
      const orderId = paymentDetails.purchase_units[0].reference_id;
      const payload = {
        orderId,
        transactionId: paymentData.orderID,
        paymentStatus: paymentDetails.status
      };
      const config = {
        timeout: 140000,
        headers: {
          'Content-Type': 'application/json',
          'x-panel-portal': site.id,
          'X-NAVENT-APP': 'publicador',
          sessionId: Cookies.get('sessionId')
        }
      };
      try {
        const { data } = await axios.post(
          `${process.env.REACT_APP_API_ECOMMERCE}/ecommerce/api/v1/orders/activate`,
          payload,
          config
        );
        Cookies.set(`selectedPlan${postingId}`, `${product.publicationPlanId}_0_${product.type.toLowerCase()}`, {
          expires: 1
        });
        await sendMail(data);
        history.push({
          pathname: '/publicador/planes/pago/exito',
          search: `?postingId=${postingId}`,
          state: data
        });
      } catch (err) {
        console.log('err activating order: ', err.response);
      }
    }
  };

  const onInitHandler = (data, actions) => {
    const { correlationID } = data;
    setHandshakeId(correlationID);
  };

  const onApproveHandler = async (data, actions) => {
    setToggleLoader(true);
    const details = await actions.order.capture();
    await activateOrder(data, details);
    setToggleLoader(false);
    return details;
  };

  const onErrorHandler = err => {
    setSnackBar(t('publisher.step4.module2.payment.paypal.error'), SNACKBAR_TYPE.DANGER);
    setToggleSnack(2000);
  };

  const onCancelHandler = data => {
    // Contiene el orderID fallido
  };

  const onClickHandler = () => {};

  const createOrderHandler = useCallback(
    (data, actions) => {
      return actions.order.create({
        purchase_units: [
          {
            amount: {
              value: formatPrice(paymentInfo.labelTotal),
              currency_code: currency,
              breakdown: {
                item_total: {
                  value: formatPrice(paymentInfo.labelSubtotal),
                  currency_code: currency
                },
                tax_total: {
                  value: formatPrice(paymentInfo.labelTaxes),
                  currency_code: currency
                }
              }
            },
            reference_id: order.orderId || '0',
            description: `${product.detailPlan} - ${product.id}`,
            soft_descriptor: 'Navent LTD.',
            items: [
              {
                name: product.name,
                description: product.detailPlan,
                unit_amount: {
                  currency_code: currency,
                  value: formatPrice(paymentInfo.labelSubtotal)
                },
                tax: {
                  currency_code: currency,
                  value: formatPrice(paymentInfo.labelTaxes)
                },
                quantity: '1'
              }
            ],
            custom_id: handshakeId
          }
        ],
        application_context: orderDefaults
      });
    },
    [
      handshakeId,
      paymentInfo.labelSubtotal,
      paymentInfo.labelTaxes,
      paymentInfo.labelTotal,
      product.detailPlan,
      product.name,
      product.id,
      order
    ]
  );

  const forcePaypalRender = () => {
    return paymentInfo.taxes != null && order !== null;
  };

  return (
    <Container>
      {toggleLoader && <Loader isUrbania={site.isUrbania} />}
      <PayPalScriptProvider options={paypalDefaults}>
        <PayPalButtons
          disabled={!paymentInfo.taxes || !order || !enabled || toggleLoader}
          // prop importante ya q paypal ignora los cambios en el state por lo q los cambios en la creacion de orden
          // no se actualiza sin esto
          forceReRender={forcePaypalRender()}
          style={{
            color: 'gold'
          }}
          onError={onErrorHandler}
          onApprove={onApproveHandler}
          onCancel={onCancelHandler}
          onClick={onClickHandler}
          onInit={onInitHandler}
          createOrder={createOrderHandler}
        />
      </PayPalScriptProvider>
    </Container>
  );
}

export default PaypalCheckout;
